import { useCallback, FC, useContext, useState, useEffect } from 'react';
import {
  Dialog as DialogComponent,
  DialogProps,
  DialogNames,
  DialogContext,
} from '@/_shared/components/Dialog';
import { dialogContext } from '@/_shared/components/Dialog/context';

// Map to store subscribers for each dialog name
const dialogSubscribers = new Map<string, Set<() => void>>();

const notifySubscribers = (name: string) => {
  const subscribers = dialogSubscribers.get(name);
  if (subscribers) {
    subscribers.forEach(callback => callback());
  }
};

const subscribe = (name: string, callback: () => void) => {
  if (!dialogSubscribers.has(name)) {
    dialogSubscribers.set(name, new Set());
  }
  dialogSubscribers.get(name)!.add(callback);
  return () => {
    dialogSubscribers.get(name)?.delete(callback);
  };
};

/**
 * @description
 * useDialog hook to manage the state and lifecycle of a dialog component.
 * The only required props for the Dialog are name and the Dialog's children.
 *
 * Now supports passing an arbitrary payload to the Dialog via openDialog(data).
 *
 * @param {DialogNames} name - The name assigned to identify the dialog.
 * @param {boolean} [avoidDestroy] - `optional` - Flag to indicate if the dialog should not be destroyed when closed.
 *
 * @returns {object} `{ isOpen, payload, openDialog, closeDialog, destroyDialog, Dialog }`
 *   - isOpen: boolean
 *   - payload: the last data object passed to openDialog (or undefined)
 *   - openDialog(data?): open the dialog and optionally set payload
 *   - closeDialog(): close (and reset payload)
 *   - destroyDialog(): remove dialog entry from context
 *   - Dialog: the React component to render
 *
 * @example
 * const { Dialog, openDialog, closeDialog, payload } = useDialog<MyData>('myDialog');
 *
 * return (
 *   <>
 *     <button onClick={() => openDialog({ userId: '42', mode: 'edit' })}>
 *       Apri in modalità edit
 *     </button>
 *     <Dialog>
 *       {(data) => (
 *         <div>
 *           <h1>{data.mode === 'edit' ? 'Modifica' : 'Visualizza'}</h1>
 *           <UserForm userId={data.userId} mode={data.mode} />
 *         </div>
 *       )}
 *     </Dialog>
 *   </>
 * );
 *
 *
 * How to augment DialogKeys in your app:
 * create a file dialogNames.d.ts with the following content or if already exists add the following content:
 * @example
 *
 * import '@/_shared/components/Dialog/types/Dialog';
 *
 * declare module '@/_shared/components/Dialog/types/Dialog' {
 *   export interface DialogKeys {
 *     dialogName1: string;
 *     dalogName2: string;
 *   }
 * }
 *
 * @dependencies
 * React, dialogContext, DialogComponent, DialogProps, Omit, FC.
 */
export const useDialog = <T,>(name: DialogNames, avoidDestroy?: boolean) => {
  const context = useContext(dialogContext) as DialogContext<T | undefined>;

  const [isOpen, setIsOpen] = useState<boolean>(() => context[name]?.isOpen ?? false);
  const [payload, setPayload] = useState<T | undefined>(context[name]?.payload ?? undefined);

  const openDialog = useCallback(
    (data?: T) => {
      if (!context[name]) {
        // Context not yet initialized, initialize it now
        context[name] = {
          Dialog: null as never,
          openDialog: () => {},
          closeDialog: () => {},
          isOpen: false,
          payload: undefined,
        };
      }
      context[name].isOpen = true;
      context[name].payload = data;
      setPayload(data);
      setIsOpen(true);
      notifySubscribers(name);
    },
    [context, name],
  );

  const closeDialog = useCallback(() => {
    context[name].isOpen = false;
    context[name].payload = undefined;
    setIsOpen(false);
    setPayload(undefined); // resetto i dati al close
    notifySubscribers(name);
  }, [context, name]);

  const destroyDialog = useCallback(() => {
    delete context[name];
    setIsOpen(false);
    setPayload(undefined);
  }, [context, name]);

  const Dialog = useCallback<FC<Omit<DialogProps, 'name'>>>(
    ({ children, ...props }) => {
      const closeFn = props.onClose ?? closeDialog;

      return (
        <DialogComponent
          name={name}
          title={props.title}
          header={props.header}
          closeButton={props.closeButton}
          closeButtonProps={props.closeButtonProps}
          open={context[name].isOpen}
          avoidCloseOnBackDrop={props.avoidCloseOnBackDrop}
          onClose={closeFn}
          dialogStyle={props.dialogStyle}
          backDropStyle={props.backDropStyle}
          titleStyle={props.titleStyle}
          headerStyle={props.headerStyle}
          destroyDialog={destroyDialog}
          avoidDestroy={avoidDestroy}
          innerBoxTopMargin={props.innerBoxTopMargin}
          data-testid={props['data-testid']}
          maxWidth={props.maxWidth}
          specialsComponentsInHeader={props.specialsComponentsInHeader}
        >
          {children}
        </DialogComponent>
      );
    },
    [closeDialog, context, destroyDialog, avoidDestroy, name],
  );

  // Subscribe to dialog state changes from other components
  useEffect(() => {
    const unsubscribe = subscribe(name, () => {
      const contextIsOpen = context[name]?.isOpen ?? false;
      const contextPayload = context[name]?.payload;
      setIsOpen(contextIsOpen);
      setPayload(contextPayload);
    });
    return unsubscribe;
  }, [context, name]);

  useEffect(() => {
    return () => {
      if (!avoidDestroy) {
        destroyDialog();
      }
    };
  }, [avoidDestroy, destroyDialog]);

  if (!context[name]) {
    context[name] = { Dialog, openDialog, closeDialog, isOpen, payload };
  } else {
    // Always update the functions to ensure the latest references are used
    context[name].openDialog = openDialog;
    context[name].closeDialog = closeDialog;
    context[name].Dialog = Dialog;
  }

  return { ...context[name] };
};
